import { ChevronRight, History, ScrollText, Waypoints } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, isAbortError, type Correlation, type LogLine } from '../../api';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Section, type Column } from '../../components/blocks';
import { useOpsReason } from '../../api/opsReason';
import { DataError, isNotConfigured, OpsReasonBar, ReasonPrompt } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { DomainLink, PlainId, RegistryTxLink } from '../../components/Ids';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { logsLink, MAX_RANGE_DAYS, traceSearchLink } from '../../lib/opsQuery';
import { useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { RegistryExchanges } from '../registry/RegistryExchanges';
import { lineKey, LogRow, prettyLine } from './LogsPage';
import { COMMAND_TONE, EnumBadge, humanize, LinkButton, WORKFLOW_TONE } from './shared';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** The server's clTRID rule (`registry_exchange.rs` `valid_cltrid`). */
const CLTRID_RE = /^[A-Za-z0-9._:-]{1,64}$/;
/** Loki stream (and log `target`) of the registry exchange events. */
const EXCHANGE_STREAM = 'registry_exchange';
const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;
/** Padding around the flow's records: the request line comes just before the first, worker lines just after the last. */
const MARGIN_MS = 15 * MINUTE_MS;
/** Lines per log query; older ones are loaded on request. */
const LOG_PAGE = 500;
/** Workflow states after which a flow writes no more lines. */
const WORKFLOW_DONE = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);

type TabId = 'logs' | 'events' | 'workflows' | 'transactions' | 'audit' | 'published';
type Tx = Correlation['registry_transactions'][number];
interface FlowWindow {
  start: string;
  end: string;
  /** Nothing recorded to scope it by: the last 7 days. */
  fallback: boolean;
}

/**
 * Where the flow's logs are: its earliest to latest record (domain events, registry commands,
 * audit, published events) with a margin, up to now while a workflow or command is still open,
 * within the API's 30-day range; the last 7 days when nothing is recorded.
 */
function flowWindow(c: Correlation, now: number): FlowWindow {
  const iso = (ms: number) => new Date(ms).toISOString();
  const times = [
    ...c.domain_events.map((e) => e.occurred_at),
    ...c.registry_transactions.flatMap((x) => [x.started_at, x.completed_at]),
    ...c.audit.map((a) => a.created_at),
    ...c.events_published.map((e) => e.created_at),
  ]
    .map((t) => (t ? new Date(t).getTime() : Number.NaN))
    .filter((t) => Number.isFinite(t));
  if (!times.length) return { start: iso(now - 7 * DAY_MS), end: iso(now), fallback: true };
  const open = c.workflows.some((w) => !WORKFLOW_DONE.has(w.state)) || c.registry_transactions.some((x) => !x.completed_at);
  const end = open ? now : Math.min(now, Math.max(...times) + MARGIN_MS);
  const start = Math.min(Math.max(Math.min(...times) - MARGIN_MS, end - MAX_RANGE_DAYS * DAY_MS + MINUTE_MS), end - MINUTE_MS);
  return { start: iso(start), end: iso(end), fallback: false };
}

interface FlowLogs {
  status: 'loading' | 'ready' | 'error';
  /** Newest first, as the API returns them. */
  lines: LogLine[];
  /** The last page came back full: older lines may remain. */
  more: boolean;
  scrubbed: boolean;
  busy: boolean;
  /** The first page's failure, or the last "load older" failure. */
  error: unknown;
}

const LOADING: FlowLogs = { status: 'loading', lines: [], more: false, scrubbed: false, busy: false, error: null };

/** The flow's log lines over its window, a page at a time (`POST /ops/v1/logs/query` returns the newest first). */
function useFlowLogs(query: string, win: FlowWindow) {
  const [s, setS] = useState<FlowLogs>(LOADING);
  const [attempt, setAttempt] = useState(0);
  const page = (end: string, signal?: AbortSignal) => api.queryLogs({ query, start: win.start, end, limit: LOG_PAGE }, { signal });

  useEffect(() => {
    const controller = new AbortController();
    setS(LOADING);
    page(win.end, controller.signal).then(
      (r) => setS({ status: 'ready', lines: r.items, more: r.items.length >= LOG_PAGE, scrubbed: r.scrubbed, busy: false, error: null }),
      (error: unknown) => {
        if (!isAbortError(error)) setS((p) => ({ ...p, status: 'error', error }));
      },
    );
    return () => controller.abort();
    // `page` reads only the values listed.
    // eslint-disable-next-line react/exhaustive-deps
  }, [query, win.start, win.end, attempt]);

  const loadOlder = () => {
    const oldest = s.lines[s.lines.length - 1];
    if (!oldest || s.busy) return;
    setS((p) => ({ ...p, busy: true, error: null }));
    // Up to the oldest line's millisecond, inclusive; lines already shown are dropped.
    page(new Date(new Date(oldest.timestamp).getTime() + 1).toISOString()).then(
      (r) =>
        setS((p) => {
          const seen = new Set(p.lines.map(lineKey));
          const fresh = r.items.filter((l) => !seen.has(lineKey(l)));
          return { ...p, lines: [...p.lines, ...fresh], more: fresh.length > 0 && r.items.length >= LOG_PAGE, busy: false };
        }),
      (error: unknown) => setS((p) => ({ ...p, busy: false, error })),
    );
  };
  return { ...s, loadOlder, retry: () => setAttempt((n) => n + 1) };
}

type ExchangeItem = { kind: 'exchange'; key: string; tx: Tx | null; cltrid: string | null; at: string; lines: LogLine[] };
type LogItem = { kind: 'line'; l: LogLine } | ExchangeItem;

/**
 * Oldest first, in the order the flow ran. Registry exchange lines are gathered under their
 * registry command (the `registry_transaction_id` they carry, else their clTRID) where the first
 * of them falls, so the login of a new session sits with the command it carried.
 */
function logItems(lines: readonly LogLine[], txs: readonly Tx[]): LogItem[] {
  const items: LogItem[] = [];
  const groups = new Map<string, ExchangeItem>();
  for (const l of [...lines].reverse()) {
    const { json } = prettyLine(l.line);
    if (l.labels.stream !== EXCHANGE_STREAM && json?.target !== EXCHANGE_STREAM) {
      items.push({ kind: 'line', l });
      continue;
    }
    // A `| json` query also returns the fields as labels, unscrubbed: the ids match there first.
    const ids: unknown[] = [l.labels.registry_transaction_id, json?.registry_transaction_id];
    const cltrids: unknown[] = [l.labels.cltrid, json?.cltrid];
    const tx = txs.find((x) => ids.includes(x.id)) ?? txs.find((x) => cltrids.includes(x.cltrid)) ?? null;
    const cltrid = tx?.cltrid ?? cltrids.find((v): v is string => typeof v === 'string' && CLTRID_RE.test(v)) ?? null;
    const key = tx?.id ?? cltrid ?? lineKey(l);
    let g = groups.get(key);
    if (!g) {
      g = { kind: 'exchange', key, tx, cltrid, at: tx?.started_at ?? l.timestamp, lines: [] };
      groups.set(key, g);
      items.push(g);
    }
    g.lines.push(l);
  }
  return items;
}

const when = (iso: string | null) => <span className="whitespace-nowrap">{formatDateTime(iso)}</span>;

/** `/ops/correlate/:id`: everything one correlation ID touched, its log lines included (spec §116). */
export function CorrelationPage() {
  const { t } = useTranslation('ops');
  const { id = '' } = useParams();
  const cid = id.toLowerCase();
  useDocumentTitle(`${t('correlation.title')} ${cid.slice(0, 8)}`);
  const valid = UUID_RE.test(cid);
  // A correlation ID is an identifier: the Observability API answers only with a reason (spec §122), so nothing is sent before one.
  const reason = useOpsReason();
  const result = useApi((signal) => (valid && reason ? api.correlate(cid, { signal }) : Promise.resolve(null)), [cid, valid, reason]);
  const crumbs = [{ label: t('crumb') }, { label: t('correlation.title') }];

  return (
    <>
      <PageHeader title={t('correlation.title')} crumbs={crumbs} subtitle={<CopyText value={cid} />} />
      <div className="space-y-6">
        {!valid ? <Callout tone="warn">{t('correlation.invalid', { id })}</Callout> : null}
        {valid && reason ? <OpsReasonBar /> : null}
        {valid && !reason ? <ReasonPrompt onSubmitted={result.reload} /> : null}
        {valid && reason && result.status === 'loading' ? <Loading label={t('correlation.loading')} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('correlation.failed')} /> : null}
        {result.status === 'success' && result.data ? <CorrelationBody c={result.data} /> : null}
      </div>
    </>
  );
}

function CorrelationBody({ c }: { c: Correlation }) {
  const { t } = useTranslation('ops');
  const L = useLabels();
  // Fixed for this view (a reload remounts): the queries must not move with the clock between renders.
  const [win] = useState(() => flowWindow(c, Date.now()));
  const range = { kind: 'custom' as const, start: win.start, end: win.end };
  const logs = useFlowLogs(c.suggested_queries.logs, win);
  // One search tells whether traces are kept here: the API answers BACKEND_NOT_CONFIGURED before it audits anything.
  const traceProbe = useApi(
    (signal) => api.searchTraces({ tags: c.suggested_queries.traces.tags, start: win.start, end: win.end, limit: 1 }, { signal }),
    [c.correlation_id, win.start, win.end],
  );
  const showTraces = traceProbe.status === 'success' || (traceProbe.status === 'error' && !isNotConfigured(traceProbe.error));
  const [picked, setPicked] = useState<TabId | null>(null);
  const domainNames = new Map(c.domain_events.map((e) => [e.domain_id, e.domain]));
  const domains = [...domainNames.entries()];

  const eventCols: Column<Correlation['domain_events'][number]>[] = [
    { key: 'when', header: t('correlation.col.when'), cell: (e) => when(e.occurred_at) },
    { key: 'domain', header: t('correlation.col.domain'), cell: (e) => <DomainLink keyOrName={e.domain} /> },
    { key: 'seq', header: t('correlation.col.sequence'), numeric: true, cell: (e) => e.sequence },
    { key: 'event', header: t('correlation.col.event'), cell: (e) => <code className="text-xs">{e.event_type}</code> },
    { key: 'request', header: t('correlation.col.request'), cell: (e) => <PlainId id={e.request_id} /> },
  ];
  const workflowCols: Column<Correlation['workflows'][number]>[] = [
    {
      key: 'wf',
      header: t('correlation.col.workflow'),
      cell: (w) => (
        <EntityRef
          label={humanize(w.workflow_type)}
          sub={w.resource_name ?? undefined}
          to={`/ops/workflows/${encodeURIComponent(w.id)}`}
          id={w.id}
        />
      ),
    },
    { key: 'state', header: t('correlation.col.state'), cell: (w) => <EnumBadge group="workflowState" value={w.state} tones={WORKFLOW_TONE} /> },
    { key: 'step', header: t('correlation.col.step'), cell: (w) => <code className="text-xs">{w.current_step ?? '—'}</code> },
    { key: 'failure', header: t('correlation.col.failure'), cell: (w) => (w.failure_code ? <code className="text-xs text-critical">{w.failure_code}</code> : <span className="text-muted-foreground">—</span>) },
    {
      key: 'resource',
      header: t('correlation.col.resource'),
      cell: (w) => {
        const name = w.resource_name ?? (w.resource_id ? domainNames.get(w.resource_id) : undefined);
        return name ? (
          <DomainLink keyOrName={name} />
        ) : (
          <span className="text-sm">
            {w.resource_type ?? '—'} <PlainId id={w.resource_id} />
          </span>
        );
      },
    },
  ];
  const txCols: Column<Tx>[] = [
    { key: 'started', header: t('correlation.col.started'), cell: (x) => when(x.started_at) },
    { key: 'op', header: t('correlation.col.operation'), cell: (x) => (
        <span title={x.operation}>
          <RegistryTxLink id={x.id} label={L.eppCommand(x.operation)} />
        </span>
      ),
    },
    { key: 'state', header: t('correlation.col.state'), cell: (x) => <EnumBadge group="commandState" value={x.state} tones={COMMAND_TONE} /> },
    {
      key: 'trid',
      header: t('correlation.col.trid'),
      cell: (x) => (
        <span className="font-mono text-xs">
          {x.cltrid}
          <span className="block text-muted-foreground">{x.svtrid ?? t('correlation.noSvtrid')}</span>
        </span>
      ),
    },
    { key: 'result', header: t('correlation.col.result'), cell: (x) => (x.result_code !== null ? <code className="text-xs">{x.result_code}</code> : <span className="text-muted-foreground">—</span>) },
    { key: 'completed', header: t('correlation.col.completed'), cell: (x) => when(x.completed_at) },
  ];
  const auditCols: Column<Correlation['audit'][number]>[] = [
    { key: 'when', header: t('correlation.col.when'), cell: (a) => when(a.created_at) },
    { key: 'action', header: t('correlation.col.action'), cell: (a) => <code className="text-xs">{a.action}</code> },
    {
      key: 'resource',
      header: t('correlation.col.resource'),
      cell: (a) => {
        const name = a.resource_id ? domainNames.get(a.resource_id) : undefined;
        if (name) return <DomainLink keyOrName={name} />;
        return (
          <span className="text-sm">
            {a.resource_type} <PlainId id={a.resource_id} />
          </span>
        );
      },
    },
    { key: 'actor', header: t('correlation.col.actor'), cell: (a) => <code className="text-xs">{a.actor_type.toLowerCase()}</code> },
    { key: 'outcome', header: t('correlation.col.outcome'), cell: (a) => <EnumBadge group="auditOutcome" value={a.outcome} tones={{ SUCCESS: 'ok', DENIED: 'warn', FAILURE: 'crit', ERROR: 'crit' }} /> },
  ];
  const publishedCols: Column<Correlation['events_published'][number]>[] = [
    { key: 'created', header: t('correlation.col.created'), cell: (e) => when(e.created_at) },
    { key: 'type', header: t('correlation.col.eventType'), cell: (e) => <code className="text-xs">{e.event_type}</code> },
    { key: 'status', header: t('correlation.col.status'), cell: (e) => <EnumBadge group="eventStatus" value={e.status} tones={{ PUBLISHED: 'ok', FAILED: 'crit', PENDING: 'pending' }} /> },
  ];

  const logLabel = logs.status === 'ready' ? `${logs.lines.length}${logs.more ? '+' : ''}` : logs.status === 'error' ? '!' : '…';
  const tabs: { id: TabId; count: number; label?: string; body: ReactNode }[] = [
    { id: 'logs', count: logs.lines.length, label: logLabel, body: <FlowLogsPanel logs={logs} win={win} query={c.suggested_queries.logs} txs={c.registry_transactions} /> },
    { id: 'events', count: c.domain_events.length, body: <DataTable columns={eventCols} rows={c.domain_events} rowKey={(e) => `${e.domain_id}/${e.sequence}`} /> },
    { id: 'workflows', count: c.workflows.length, body: <DataTable columns={workflowCols} rows={c.workflows} rowKey={(w) => w.id} /> },
    {
      id: 'transactions',
      count: c.registry_transactions.length,
      body: <DataTable columns={txCols} rows={c.registry_transactions} rowKey={(x) => x.id} expand={(x) => <ExchangeText cltrid={x.cltrid} startedAt={x.started_at} />} />,
    },
    { id: 'audit', count: c.audit.length, body: <DataTable columns={auditCols} rows={c.audit} rowKey={(a) => `${a.created_at}/${a.action}/${a.resource_id ?? ''}`} /> },
    { id: 'published', count: c.events_published.length, body: <DataTable columns={publishedCols} rows={c.events_published} rowKey={(e) => `${e.event_type}/${e.created_at}`} /> },
  ];
  const firstRecords = tabs.find((x) => x.id !== 'logs' && x.count > 0);
  // The log lines first: they are what staff open this page for. No line at all: the first tab with records.
  const active = picked ?? (logs.status === 'ready' && logs.lines.length === 0 && firstRecords ? firstRecords.id : 'logs');

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {domains.map(([domainId, name]) => (
          <LinkButton key={domainId} to={`/domains/${encodeURIComponent(name)}/history`}>
            <History /> {t('correlation.follow.history', { domain: name })}
          </LinkButton>
        ))}
        {showTraces ? (
          <LinkButton to={traceSearchLink(c.suggested_queries.traces.tags, range)}>
            <Waypoints /> {t('correlation.follow.traces')}
          </LinkButton>
        ) : null}
        <LinkButton to={logsLink(c.suggested_queries.logs, range)} variant="ghost">
          <ScrollText /> {t('correlation.follow.logs')}
        </LinkButton>
      </div>

      {!firstRecords ? <Callout title={t('correlation.emptyTitle')}>{t('correlation.emptyBody')}</Callout> : null}

      <Tabs value={active} onValueChange={(v) => setPicked(v as TabId)}>
        <div className="overflow-x-auto">
          <TabsList>
            {tabs.map((x) => (
              <TabsTrigger key={x.id} value={x.id} disabled={x.id !== 'logs' && x.count === 0}>
                {t(`correlation.tab.${x.id}`)}
                <Badge tone={x.count ? 'info' : 'neutral'} icon={false} className="ml-1 px-1.5 py-0">
                  {x.label ?? x.count}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {tabs.map((x) => (
          <TabsContent key={x.id} value={x.id}>
            <Section flush className="overflow-hidden pt-0">
              {x.body}
            </Section>
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
}

/** The Log tab: every line of the flow, oldest first, registry exchanges with their text on request. */
function FlowLogsPanel({ logs, win, query, txs }: { logs: ReturnType<typeof useFlowLogs>; win: FlowWindow; query: string; txs: readonly Tx[] }) {
  const { t } = useTranslation('ops');
  if (logs.status === 'loading') {
    return (
      <div className="px-5 py-4">
        <Loading label={t('correlation.logs.loading')} />
      </div>
    );
  }
  if (logs.status === 'error') {
    return (
      <div className="px-5 py-4">
        <DataError error={logs.error} onRetry={logs.retry} title={t('correlation.logs.failed')} />
      </div>
    );
  }
  const items = logItems(logs.lines, txs);
  const span = { start: formatDateTime(win.start), end: formatDateTime(win.end) };
  return (
    <>
      <div className="space-y-1 px-5 py-3 text-xs text-muted-foreground">
        <p role="status">{[t('correlation.logs.summary', { count: logs.lines.length }), logs.scrubbed ? t('logs.scrubbed') : null].filter(Boolean).join(' · ')}</p>
        <p>{win.fallback ? t('correlation.logs.fallback', span) : t('correlation.logs.window', span)}</p>
      </div>
      {logs.more ? (
        <div className="flex flex-wrap items-center gap-3 border-t px-5 py-2 text-xs text-muted-foreground">
          <Button type="button" variant="outline" size="sm" onClick={logs.loadOlder} disabled={logs.busy}>
            {t('correlation.logs.older')}
          </Button>
          {t('correlation.logs.capped', { limit: LOG_PAGE })}
        </div>
      ) : null}
      {logs.error ? (
        <div className="px-5 pb-3">
          <DataError error={logs.error} onRetry={logs.loadOlder} title={t('correlation.logs.failed')} />
        </div>
      ) : null}
      {items.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState title={t('correlation.logs.none')} />
        </div>
      ) : (
        <ol className="divide-y border-t">
          {items.map((it, i) =>
            it.kind === 'line' ? <LogRow key={`${lineKey(it.l)}#${i}`} l={it.l} correlation={false} /> : <ExchangeGroup key={`x/${it.key}`} g={it} />,
          )}
        </ol>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t px-5 py-3 text-xs text-muted-foreground">
        {t('correlation.logs.query')}
        <code className="min-w-0 rounded bg-muted px-1.5 py-0.5 break-all text-foreground">{query}</code>
      </div>
    </>
  );
}

/** The exchange lines of one registry command, then their text. */
function ExchangeGroup({ g }: { g: ExchangeItem }) {
  const { t } = useTranslation('ops');
  const L = useLabels();
  return (
    <li className="border-l-2 border-l-info bg-muted/20">
      <div className="flex flex-wrap items-center gap-2 px-5 pt-3 text-xs">
        <span className="font-medium">{g.tx ? t('correlation.exchange.group') : t('correlation.exchange.ungrouped')}</span>
        {g.tx ? (
          <>
            <span title={g.tx.operation}>
              <RegistryTxLink id={g.tx.id} label={L.eppCommand(g.tx.operation)} />
            </span>
            <EnumBadge group="commandState" value={g.tx.state} tones={COMMAND_TONE} />
          </>
        ) : null}
        {g.cltrid ? <code className="rounded bg-muted px-1.5 py-0.5">{g.cltrid}</code> : null}
      </div>
      <ol className="divide-y">
        {g.lines.map((l, i) => (
          <LogRow key={`${lineKey(l)}#${i}`} l={l} correlation={false} />
        ))}
      </ol>
      <div className="px-5 pb-3">
        <ExchangeText cltrid={g.cltrid} startedAt={g.at} />
      </div>
    </li>
  );
}

/**
 * "Nội dung trao đổi": the request / response as exchanged with the registry, read by clTRID and
 * opened server-side (`GET /ops/v1/registry-exchanges/{cltrid}`, `registry.payload.raw.view`, the
 * page's reason). The payload holds contact data and every view is audited, so it is read when
 * opened, not on every visit.
 */
function ExchangeText({ cltrid, startedAt }: { cltrid: string | null; startedAt: string }) {
  const { t } = useTranslation('ops');
  const [open, setOpen] = useState(false);
  return (
    <details className="group rounded-lg border bg-background" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-1.5 gap-y-0.5 px-3 py-2 text-sm font-medium select-none [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-4 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
        {t('correlation.exchange.title')}
        <span className="text-xs font-normal text-muted-foreground">{t('correlation.exchange.hint')}</span>
      </summary>
      {open ? (
        <div className="border-t p-3">
          {cltrid ? <RegistryExchanges cltrid={cltrid} startedAt={startedAt} /> : <p className="text-sm text-muted-foreground">{t('correlation.exchange.noCltrid')}</p>}
        </div>
      ) : null}
    </details>
  );
}
